import React, { useState } from "react";
import { Play, ArrowRight, BookOpen, Layers, ShieldCheck } from "lucide-react";

// Verbatim/near-verbatim excerpts from the Module 1 source of truth.
const lessons = [
    {
        id: "the-ai-umbrella",
        badge: "Lesson 01",
        icon: BookOpen,
        title: "The AI Umbrella: What AI Is and Is Not",
        body: "Artificial intelligence is an umbrella term for computer systems designed to perform tasks that usually require some form of human perception, prediction, classification, language use, or decision support. Generative AI is one part of that umbrella — its defining feature is that it produces new content. A large language model is a type of generative AI trained to work with language.",
        takeaway:
            "Do not ask only, \"Is this AI?\" Ask, \"What kind of system is this, what information can it access, and what is it being asked to do?\"",
    },
    {
        id: "prediction-not-thought",
        badge: "Lesson 02",
        icon: Layers,
        title: "Prediction, Not Thought: Tokens & Next-Token Generation",
        body: "A large language model works with tokens — small units of text. When it responds, it estimates a probability distribution over possible next tokens, selects one, adds it to the sequence, and repeats. The model is not pausing to form a human belief and then reporting it. It is extending a sequence in a statistically plausible way.",
        takeaway:
            "A polished paragraph is evidence that the model can generate polished language. It is not, by itself, evidence that the claims are correct, current, fair, or aligned with your course.",
    },
    {
        id: "why-confident-ai-can-be-wrong",
        badge: "Lesson 04",
        icon: ShieldCheck,
        title: "Why Confident AI Can Be Wrong",
        body: "A response can be grammatically strong, logically arranged, and factually wrong at the same time. Common failure modes include fabrication, outdated information, omission, bias, context mismatch, instruction drift, false precision, and reasoning errors. Match your review depth to the risk of the decision.",
        takeaway:
            "Never interpret confidence, length, polish, or specificity as a substitute for evidence. The more consequential the decision, the more direct the human review must be.",
    },
];

const MODULE1_STRIPE_LINK = "https://buy.stripe.com/7sYbJ14Q305E8szc6Y1kA01";

export default function ModulePreview() {
    const [active, setActive] = useState(lessons[0].id);
    const lesson = lessons.find((l) => l.id === active) || lessons[0];
    const Icon = lesson.icon;

    return (
        <section
            data-testid="module-preview-section"
            className="border-b border-black/10 bg-secondary"
        >
            <div className="container-edge py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-7">
                        <p className="overline">Inside Module 1</p>
                        <h2 className="mt-3 font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
                            A dependable mental model for<br />
                            <span className="text-klein">AI in faculty work.</span>
                        </h2>
                    </div>
                    <p className="lg:col-span-4 lg:col-start-9 text-muted-foreground self-end">
                        By the end of Module 1 you will be able to explain how generative AI works in practical terms,
                        identify its most important limits, and use it for one faculty task with clear boundaries and a
                        repeatable review process.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-black/10 border border-black/10">
                    {/* Lesson selector */}
                    <div className="lg:col-span-4 bg-white">
                        {lessons.map((l) => {
                            const isActive = l.id === active;
                            const LIcon = l.icon;
                            return (
                                <button
                                    key={l.id}
                                    data-testid={`preview-lesson-${l.id}`}
                                    onClick={() => setActive(l.id)}
                                    className={`w-full text-left p-6 lg:p-8 border-b border-black/10 last:border-b-0 transition-colors group ${
                                        isActive ? "bg-klein text-white" : "hover:bg-black/[0.03]"
                                    }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <span
                                            className={`shrink-0 inline-flex items-center justify-center w-9 h-9 ${
                                                isActive ? "bg-white text-klein" : "bg-klein text-white"
                                            }`}
                                        >
                                            <LIcon size={18} />
                                        </span>
                                        <div>
                                            <div className={`font-mono text-[10px] tracking-[0.22em] uppercase ${isActive ? "text-white/70" : "text-klein"}`}>
                                                {l.badge}
                                            </div>
                                            <div className="mt-1 font-heading font-bold text-lg tracking-tight leading-snug">
                                                {l.title}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Lesson body */}
                    <div className="lg:col-span-8 bg-white p-8 lg:p-12 flex flex-col">
                        <div className="flex items-center gap-3">
                            <Play className="text-klein" size={18} />
                            <span className="overline">Module 1 · Preview</span>
                        </div>
                        <h3
                            data-testid="preview-active-title"
                            className="mt-5 font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight"
                        >
                            {lesson.title}
                        </h3>
                        <div className="mt-6 flex items-start gap-4">
                            <Icon className="text-klein mt-1 shrink-0" size={20} />
                            <p className="text-base lg:text-lg leading-relaxed text-foreground/85">
                                {lesson.body}
                            </p>
                        </div>

                        <div className="mt-8 border-l-2 border-klein pl-5">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Key takeaway</div>
                            <div className="mt-1 font-heading font-bold text-xl tracking-tight leading-snug">
                                {lesson.takeaway}
                            </div>
                        </div>

                        <div className="mt-auto pt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <p className="text-sm text-muted-foreground">
                                Full Module 1 includes 7 lessons, 7 worksheets, knowledge checks, an answer key, and a
                                20-prompt copy-and-paste library.
                            </p>
                            <a
                                href={MODULE1_STRIPE_LINK}
                                data-testid="preview-cta-buy"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors whitespace-nowrap self-start sm:self-auto"
                            >
                                Get full Module — $99 <ArrowRight size={16} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
